import * as MutationTypes from "../mutation_types"
import { State, AuthHeaders, MonthlyReportMail, User } from "../store_types"
import { callMonthlyReportMailsFindAPI, callFixMonthlyReportMailsAPI, callSaveDraftMonthlyReportMailsAPI, callCancelMonthlyReportMailAPI } from "../../api/monthly_report_mails"
import { HTTP_STATUS_NO_CONTENT, HTTP_STATUS_UNPROCESSABLE_ENTITY } from "../../api/index"
import { AxiosResponseHeaders, AxiosError } from "axios"

const getDefaultState  = () => {
  return {
    monthlyReportMail: null,
    monthlyReportMailPostSuccess : null,
    monthlyReportMailErrors: []
  }
}

const monthlyReportMails = {
  state: () => getDefaultState(),

  getters: {
    monthlyReportMail (state : State) {
      return state.monthlyReportMail
    },
    monthlyReportMailPostSuccess (state : State) {
      return state.monthlyReportMailPostSuccess
    },
    monthlyReportMailEditable (state : State) {
      return state.monthlyReportMailEditable
    },
    monthlyReportMailErrors (state : State) {
      return state.monthlyReportMailErrors
    },
  },

  actions: {
    fetchMonthlyReportMail({ commit, getters }: { commit: (mutationType: string, resultData: AxiosResponseHeaders | MonthlyReportMail | boolean | Array<string>) => void, getters: { [authHeaders: string]: AuthHeaders | number | Date | User}}){
      const currentUser = getters.currentUser as User
      if( currentUser.type != "Sales") return

      return new Promise<void>((resolve, reject) => {
        callMonthlyReportMailsFindAPI(
          getters.authHeaders as AuthHeaders,
          {
            corporationId: getters.selectedCorporationId as number,
            targetMonth: getters.selectedMonthlyReportMailDate as Date
          },
          // Success
          (headers: AxiosResponseHeaders, monthlyReportMail: MonthlyReportMail) => {
            commit(MutationTypes.ASSIGN_AUTH_HEADERS, headers)
            commit(MutationTypes.ASSIGN_MONTHLY_REPORT_MAIL, monthlyReportMail)
            // 新規作成時のみ編集画面を表示する
            if(monthlyReportMail.newRecord) {
              commit(MutationTypes.ASSIGN_MONTHLY_REPORT_MAIL_EDITABLE, true)
            } else {
              commit(MutationTypes.ASSIGN_MONTHLY_REPORT_MAIL_EDITABLE, false)
            }
            // エラーメッセージのクリア
            commit(MutationTypes.ASSIGN_POST_MONTHLY_REPORT_MAIL_RESULT, true)
            commit(MutationTypes.ASSIGN_MONTHLY_REPORT_MAIL_ERRORS, [])
            resolve()
          },
          // Failure
          () => {
            reject()
          })
      })
    },

    fixMonthlyReportMail({ dispatch, commit, getters }: { dispatch: (actionName: string) => void, commit: (mutationType: string, result: AxiosResponseHeaders | boolean | Array<string>) => void, getters: { [authHeaders: string]: AuthHeaders }}, monthlyReportMail: MonthlyReportMail){
      return new Promise<void>((resolve, reject) => {
        callFixMonthlyReportMailsAPI(
          getters.authHeaders as AuthHeaders,
          monthlyReportMail,
          // Success
          (headers: AxiosResponseHeaders, status: number) => {
            commit(MutationTypes.ASSIGN_AUTH_HEADERS, headers)
            let result = false
            if(status == HTTP_STATUS_NO_CONTENT) {
              result = true
            }
            commit(MutationTypes.ASSIGN_POST_MONTHLY_REPORT_MAIL_RESULT, result)
            dispatch("fetchMonthlyReportMail")
            resolve()
          },
          // Failure
          (error: AxiosError) => {
            if(error.response?.headers){
              commit(MutationTypes.ASSIGN_AUTH_HEADERS, error.response.headers)
            }
            commit(MutationTypes.ASSIGN_POST_MONTHLY_REPORT_MAIL_RESULT, false)
            if(error.response?.status == HTTP_STATUS_UNPROCESSABLE_ENTITY){
              // バリデーションエラー
              commit(MutationTypes.ASSIGN_MONTHLY_REPORT_MAIL_ERRORS, error.response?.data.messages)
              resolve()
            } else {
              reject()
            }
          })
      })
    },

    saveDraftMonthlyReportMail({ dispatch, commit, getters }: { dispatch: (actionName: string) => void, commit: (mutationType: string, result: AxiosResponseHeaders | boolean) => void, getters: { [authHeaders: string]: AuthHeaders }}, monthlyReportMail: MonthlyReportMail){
      return new Promise<void>((resolve, reject) => {
        callSaveDraftMonthlyReportMailsAPI(
          getters.authHeaders as AuthHeaders,
          monthlyReportMail,
          // Success
          (headers: AxiosResponseHeaders, status: number) => {
            commit(MutationTypes.ASSIGN_AUTH_HEADERS, headers)
            let result = false
            if(status == HTTP_STATUS_NO_CONTENT) {
              result = true
            }
            commit(MutationTypes.ASSIGN_POST_MONTHLY_REPORT_MAIL_RESULT, result)
            dispatch("fetchMonthlyReportMail")
            resolve()
          },
          // Failure
          () => {
            reject()
          })
      })
    },

    cancelMonthlyReportMail({ dispatch, commit, getters }: { dispatch: (actionName: string) => void, commit: (mutationType: string, result: AxiosResponseHeaders | boolean) => void, getters: { [authHeaders: string]: AuthHeaders }}, monthlyReportMailId: number){
      return new Promise<void>((resolve, reject) => {
        callCancelMonthlyReportMailAPI(
          getters.authHeaders as AuthHeaders,
          monthlyReportMailId,
          // Success
          (headers: AxiosResponseHeaders, status: number) => {
            commit(MutationTypes.ASSIGN_AUTH_HEADERS, headers)
            let result = false
            if(status == HTTP_STATUS_NO_CONTENT) {
              result = true
            }
            commit(MutationTypes.ASSIGN_POST_MONTHLY_REPORT_MAIL_RESULT, result)
            dispatch("fetchMonthlyReportMail")
            resolve()
          },
          // Failure
          () => {
            reject()
          })
      })
    },

    commitMonthlyReportMailEditable({ commit }: { commit: (mutationType:string, editable: boolean) => void }, editable: boolean){
      commit(MutationTypes.ASSIGN_MONTHLY_REPORT_MAIL_EDITABLE, editable)
    },

    resetMontylyReportMailsState ({ commit }: { commit: (mutationType:string) => void }) {
      commit(MutationTypes.RESET_MONTHLY_REPORT_MAILS_STATE)
    },
  },
  mutations: {
    [MutationTypes.ASSIGN_MONTHLY_REPORT_MAIL](state: State, monthlyReportMail: MonthlyReportMail) {
      state.monthlyReportMail = monthlyReportMail
    },
    [MutationTypes.ASSIGN_POST_MONTHLY_REPORT_MAIL_RESULT](state: State, result: boolean) {
      state.monthlyReportMailPostSuccess = result
    },
    [MutationTypes.ASSIGN_MONTHLY_REPORT_MAIL_EDITABLE](state: State, editable: boolean) {
      state.monthlyReportMailEditable = editable
    },
    [MutationTypes.ASSIGN_MONTHLY_REPORT_MAIL_ERRORS](state: State, messages: Array<string>) {
      state.monthlyReportMailErrors = messages
    },
    [MutationTypes.RESET_MONTHLY_REPORT_MAILS_STATE](state: State) {
      Object.assign(state, getDefaultState())
    },
  },
}

export default monthlyReportMails
